import { Alert } from '@creditornot/cb-alert';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Spinner } from '@creditornot/cb-progress';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { StringParam, useQueryParams } from 'use-query-params';

import { fetchSettlements } from 'apis/restaurant-api/merchant-admin/settlements/api';
import PageHeader from 'common/components/PageHeader';
import useCursorPagination from 'common/hooks/useCursorPagination';
import { useVenueContext } from 'modules/venue/VenueContext';

import SettlementDetailsSidePanel from './SettlementDetailsSidePanel';
import SettlementList from './SettlementList';
import { NUM_SETTLEMENTS_PER_PAGE } from './constants';

const queryParamConfigMap = {
  settlementId: StringParam,
};

const Root = styled.div`
  background-color: ${cbSemanticCssVars.surfaceMain};
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-x: auto;
`;

const SpinnerContainer = styled.div`
  display: flex;
  flex: 1 1 auto;
  justify-content: center;
  padding-block-start: 32px;
`;

const ErrorAlert = styled(Alert).attrs({ variant: 'error' })`
  margin: 24px;
`;

const List = styled(SettlementList)`
  flex: 1 1 auto;
  min-width: 1024px;
`;

const Settlements = () => {
  const venue = useVenueContext();
  const [query, setQuery] = useQueryParams(queryParamConfigMap);
  const intl = useIntl();
  const {
    data,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    status,
  } = useCursorPagination(
    `/v1/merchant-admin/venues/${venue.id}/settlements`,
    {
      venueId: venue.id,
      limit: NUM_SETTLEMENTS_PER_PAGE,
      pivotId: query.settlementId || undefined,
    },
    fetchSettlements,
    { staleTime: 60000 },
  );

  const settlements = useMemo(() => data.flatMap(entry => entry), [data]);

  const handleOpenSettlementDetails = useCallback(
    (settlementId: string) => {
      setQuery({ settlementId });
    },
    [setQuery],
  );

  const handleCloseSettlementDetails = useCallback(() => {
    setQuery({ settlementId: undefined });
  }, [setQuery]);

  const selectedSettlement =
    (query.settlementId && settlements.find(settlement => settlement.id === query.settlementId)) || null;

  return (
    <>
      <Root>
        <PageHeader title={intl.formatMessage({ id: 'navigation.settlements' })} />
        {status === 'loading' ? (
          <SpinnerContainer data-test-id="settlements-spinner">
            <Spinner size="extraLarge" />
          </SpinnerContainer>
        ) : status === 'error' ? (
          <ErrorAlert>
            <FormattedMessage id="errors.unknown-error-retry-contact" />
          </ErrorAlert>
        ) : (
          <List
            fetchingNextPage={isFetchingNextPage}
            fetchingPreviousPage={isFetchingPreviousPage}
            onFetchNextPage={hasNextPage ? fetchNextPage : undefined}
            onFetchPreviousPage={hasPreviousPage ? fetchPreviousPage : undefined}
            onClickSettlement={handleOpenSettlementDetails}
            settlements={settlements}
            selectedSettlementId={query.settlementId!}
            timezone={venue.timezone}
          />
        )}
      </Root>
      <SettlementDetailsSidePanel
        onClose={handleCloseSettlementDetails}
        settlement={selectedSettlement}
        timezone={venue.timezone}
      />
    </>
  );
};

export default Settlements;
